/**
 * SubscribePage — Plan selection and Chargebee checkout trigger.
 *
 * Shows monthly vs annual plan cards. User picks one, we call
 * POST /v1/subscription/checkout, then redirect to Chargebee hosted checkout.
 * After payment, Chargebee redirects back to /app and the webhook updates DynamoDB.
 *
 * Accessible at /app/subscribe. Shown to:
 *   - Trial users who want to subscribe early (via trial banner)
 *   - Expired trial users who need to subscribe to continue
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { isAccessActive, type SubscriptionStatusResponse, type SubscriptionSource } from '@tabletryb/shared';
import { Check, Zap, Crown, Loader2, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

interface PlanCard {
  id: string;
  name: string;
  price: string;
  period: string;
  badge?: string;
  features: string[];
}

const PLANS: PlanCard[] = [
  {
    id: 'TableTryb-USD-Monthly',
    name: 'Monthly',
    price: '$3.99',
    period: '/month',
    features: [
      'Unlimited recipes',
      'Unlimited AI recipe scans',
      'Up to 8 tribe members',
      'Up to 3 grocery stores',
      'Kroger cart push',
    ],
  },
  {
    id: 'TableTryb-USD-Yearly',
    name: 'Annual',
    price: '$39.99',
    period: '/year',
    badge: 'Save 17%',
    features: [
      'Everything in Monthly',
      'Best value — $3.33/mo effective',
      'Lock in your price for 12 months',
    ],
  },
];

const SubscribePage: React.FC = () => {
  const { user, getToken } = useAuth();
  const { post } = useApi();
  const [selectedPlan, setSelectedPlan] = useState<string>('TableTryb-USD-Monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState<boolean | null>(null);
  const [referralMessage, setReferralMessage] = useState('');

  // Existing-subscription gate (defense in depth alongside the server-side checkout guard).
  // A real, access-active subscription (Chargebee or IAP) blocks re-subscribing on web; a
  // local pre-subscription trial has no subscriptionSource and can still subscribe early.
  const [statusChecked, setStatusChecked] = useState(false);
  const [existingSource, setExistingSource] = useState<SubscriptionSource | null>(null);

  useEffect(() => {
    let cancelled = false;
    const checkStatus = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/v1/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data: SubscriptionStatusResponse = await res.json();
          if (data.subscriptionSource && isAccessActive(data.status)) {
            setExistingSource(data.subscriptionSource);
          }
        }
      } catch (err) {
        // Non-fatal: fall back to the subscribe flow; the server-side guard still protects.
        console.error('Failed to check subscription status:', err);
      } finally {
        if (!cancelled) setStatusChecked(true);
      }
    };
    checkStatus();
    return () => { cancelled = true; };
  }, [getToken]);

  const handleSubscribe = async () => {
    if (!user?.householdId) {
      setErrorMessage('No tribe found. Please create a tribe first.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setReferralMessage('');

    try {
      const token = await getToken();
      const code = referralCode.trim().toUpperCase();

      // If a referral code was entered, validate it first
      let validatedCode = '';
      if (code) {
        try {
          const valData = await post('/v1/referral/validate', { referralCode: code }) as {
            valid: boolean; referrerName?: string; message?: string;
          };
          if (valData.valid) {
            validatedCode = code;
            setReferralValid(true);
            setReferralMessage(`Referred by ${valData.referrerName}`);
          } else {
            setReferralValid(false);
            setReferralMessage(valData.message || 'Invalid referral code.');
            setIsLoading(false);
            return; // Stop — don't proceed to checkout with a bad code
          }
        } catch {
          setReferralValid(false);
          setReferralMessage('Unable to validate referral code. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      const response = await fetch(`${API_URL}/v1/subscription/checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: selectedPlan,
          householdId: user.householdId,
          redirectUrl: `${window.location.origin}/app`,
          ...(validatedCode && { referralCode: validatedCode }),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        // Active IAP subscription blocks web checkout (server-side guard, unit 4).
        if (response.status === 409 && errData.error === 'CHECKOUT_BLOCKED_IAP_ACTIVE') {
          throw new Error(
            "You're already subscribed through the app. Manage your subscription in your device's App Store or Google Play settings."
          );
        }
        throw new Error(errData.message || `Checkout failed (${response.status})`);
      }

      const data = await response.json();

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>Choose Your Plan</h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '1.05rem', margin: 0 }}>
          Subscribe to keep your tribe's meal planning running smoothly.
        </p>
      </div>

      {errorMessage && (
        <div style={{
          maxWidth: '600px',
          margin: '0 auto 1.5rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '8px',
          color: '#DC2626',
          fontSize: '0.875rem',
          textAlign: 'center',
        }}>
          {errorMessage}
        </div>
      )}

      {/* Plan Cards */}
      <div style={{
        display: 'flex',
        gap: '1.5rem',
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginBottom: '2rem',
      }}>
        {PLANS.map((plan) => {
          const isSelected = selectedPlan === plan.id;
          return (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              style={{
                position: 'relative',
                width: '280px',
                padding: '2rem 1.5rem',
                background: isSelected ? 'var(--color-surface)' : 'var(--color-bg-alt, #F9FAFB)',
                border: isSelected
                  ? '2px solid var(--color-primary)'
                  : '2px solid var(--color-border, #E5E7EB)',
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
                boxShadow: isSelected ? '0 4px 16px rgba(45, 106, 79, 0.15)' : 'none',
              }}
            >
              {/* Badge */}
              {plan.badge && (
                <span style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '16px',
                  background: 'var(--color-secondary, #E76F51)',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  padding: '2px 10px',
                  borderRadius: '10px',
                }}>
                  {plan.badge}
                </span>
              )}

              {/* Plan Name */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem',
              }}>
                {plan.id.includes('Yearly') ? <Crown size={20} /> : <Zap size={20} />}
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: 'var(--color-text)',
                }}>
                  {plan.name}
                </span>
              </div>

              {/* Price */}
              <div style={{ marginBottom: '1.25rem' }}>
                <span style={{
                  fontSize: '2rem',
                  fontWeight: 800,
                  color: isSelected ? 'var(--color-primary)' : 'var(--color-text)',
                  lineHeight: 1,
                }}>
                  {plan.price}
                </span>
                <span style={{
                  fontSize: '0.9rem',
                  color: 'var(--color-text-muted)',
                  marginLeft: '2px',
                }}>
                  {plan.period}
                </span>
              </div>

              {/* Features */}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {plan.features.map((feature, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-muted)',
                    marginBottom: '0.5rem',
                    lineHeight: 1.4,
                  }}>
                    <Check size={14} style={{
                      color: 'var(--color-success, #059669)',
                      marginTop: '2px',
                      flexShrink: 0,
                    }} />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Selected indicator */}
              {isSelected && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'var(--color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Check size={12} style={{ color: 'white' }} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Referral Code (optional, always visible) */}
      <div style={{ maxWidth: 600, margin: '0 auto 1.5rem' }}>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-text)',
          marginBottom: 6,
        }}>
          <Gift size={16} style={{ color: 'var(--color-primary)' }} />
          Referral Code <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(optional)</span>
        </label>
        <input
          className="form-input"
          placeholder="e.g. TRYB-AB12CD"
          value={referralCode}
          onChange={(e) => {
            setReferralCode(e.target.value);
            setReferralValid(null);
            setReferralMessage('');
          }}
          disabled={isLoading}
          style={{ fontSize: '0.95rem', textTransform: 'uppercase', maxWidth: '280px' }}
        />
        {referralMessage && (
          <p style={{
            margin: '4px 0 0', fontSize: 13,
            color: referralValid ? 'var(--color-success, #059669)' : 'var(--color-error, #DC2626)',
          }}>
            {referralValid && <Check size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
            {referralMessage}
          </p>
        )}
      </div>

      {/* Subscribe action — gated by the existing-subscription check (defense in depth) */}
      {!statusChecked ? (
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={20} className="spin" style={{ color: 'var(--color-text-muted)' }} />
        </div>
      ) : existingSource ? (
        <div style={{
          maxWidth: 600,
          margin: '0 auto',
          textAlign: 'center',
          padding: '1rem 1.25rem',
          background: 'var(--color-bg-alt, #F9FAFB)',
          border: '1px solid var(--color-border, #E5E7EB)',
          borderRadius: '8px',
        }}>
          <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-text)' }}>
            You're already subscribed.
          </p>
          <p style={{ margin: '6px 0 0', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
            {existingSource === 'chargebee'
              ? 'Manage your plan, payment method, or cancellation from your Profile.'
              : existingSource === 'apple_iap'
              ? 'Your subscription is managed through the App Store. On your iPhone or iPad, open Settings \u2192 your name \u2192 Subscriptions to make changes.'
              : 'Your subscription is managed through Google Play. Open the Play Store app \u2192 Menu \u2192 Subscriptions to make changes.'}
          </p>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={handleSubscribe}
            disabled={isLoading}
            style={{
              padding: '0.875rem 3rem',
              fontSize: '1.05rem',
              fontWeight: 700,
              borderRadius: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="spin" />
                Redirecting to checkout...
              </>
            ) : (
              'Subscribe Now'
            )}
          </button>

          <p style={{
            marginTop: '1rem',
            fontSize: '0.85rem',
            color: 'var(--color-text-muted)',
          }}>
            Secure checkout powered by Chargebee. Cancel anytime.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscribePage;
